import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import api from "../services/api";
import PaymentSettings from "./PaymentSettings";
import ProfileModal from "../components/ProfileModal";

function Modal({ show, onClose, title, maxWidth = "max-w-lg", children }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg w-full ${maxWidth} flex flex-col`} style={{ maxHeight: "90vh" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none font-bold">×</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

const MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];

const formatTL = (v) => `₺${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`;

// ─── ÖDEME YÖNTEMLERİ ────────────────────────────────────────────────────────
// Yöneticinin elle seçebileceği yöntemler. Kredi kartı burada YOK:
// online kart ödemesi iyzico üzerinden gelir ve otomatik "iyzico" olarak yazılır.
const PAYMENT_METHODS = [
  { value: "nakit", label: "Nakit" },
  { value: "havale_eft", label: "Havale/EFT" },
  { value: "diger", label: "Diğer" },
];

// Görüntüleme etiketleri — eski kayıtlar da doğru görünsün diye legacy değerler dahil
const PAYMENT_METHOD_LABELS = {
  nakit: "Nakit",
  havale_eft: "Havale/EFT",
  diger: "Diğer",
  // legacy (artık seçilemez, sadece geçmiş kayıtlarda görünür)
  havale: "Havale",
  eft: "EFT",
  "kredi kartı": "Kredi Kartı",
  kredi_karti: "Kredi Kartı",
  credit_card: "Kredi Kartı",
  manual: "Manuel",
  // online
  iyzico: "Kredi Kartı (Online)",
};

const paymentMethodLabel = (m) => PAYMENT_METHOD_LABELS[m] || m || "—";

// Türkçe karakter duyarsız arama ("gul" -> "Gül")
const norm = (v) => (v || "")
  .toString()
  .toLocaleLowerCase("tr")
  .replace(/ı/g, "i").replace(/ğ/g, "g").replace(/ü/g, "u")
  .replace(/ş/g, "s").replace(/ö/g, "o").replace(/ç/g, "c")
  .trim();

function ManagerDashboard() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [units, setUnits] = useState([]);
  const [overdueUnits, setOverdueUnits] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid: 0, unpaid: 0 });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState("dashboard");

  const [showModal, setShowModal] = useState(false);
  const [invoiceType, setInvoiceType] = useState("all");
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  const [showExemptModal, setShowExemptModal] = useState(false);
  const [exemptLoading, setExemptLoading] = useState(false);
  const [showMonthExemptModal, setShowMonthExemptModal] = useState(false);
  const [exemptions, setExemptions] = useState([]);
  const [exemptUnit, setExemptUnit] = useState("");
  const [exemptMonth, setExemptMonth] = useState("");
  const [exemptYear, setExemptYear] = useState(new Date().getFullYear().toString());
  const [exemptNote, setExemptNote] = useState("");

  const [showDebtModal, setShowDebtModal] = useState(false);
  const [debtDetail, setDebtDetail] = useState(null);
  const [debtLoading, setDebtLoading] = useState(false);
  const [debtDiscountType, setDebtDiscountType] = useState("percent");
  const [debtDiscountValue, setDebtDiscountValue] = useState("");
  const [debtDiscountNote, setDebtDiscountNote] = useState("");
  const [debtDiscountTarget, setDebtDiscountTarget] = useState("unit");
  const [applyingDebtDiscount, setApplyingDebtDiscount] = useState(false);

  const [filterUnit, setFilterUnit] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Bildirim
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [notifUnit, setNotifUnit] = useState(null);
  const [notifType, setNotifType] = useState("payment_overdue");
  const [notifChannels, setNotifChannels] = useState(["sms", "email", "whatsapp"]);
  const [sendingNotif, setSendingNotif] = useState(false);
  const [notifResult, setNotifResult] = useState(null);
    // Kotalı hatırlatma
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderTargets, setReminderTargets] = useState([]);
  const [reminderQuota, setReminderQuota] = useState(2);
  const [selectedReminders, setSelectedReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  // Manuel ödeme
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [payMethod, setPayMethod] = useState("nakit");
  const [payNote, setPayNote] = useState("");
  const [markingPaid, setMarkingPaid] = useState(false);
  const [paidDetail, setPaidDetail] = useState(null);
  // Sakin hesapları / şifre yönetimi
  const [residentUsers, setResidentUsers] = useState([]);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetMessage, setResetMessage] = useState(null);
  const [newUser, setNewUser] = useState({ full_name: "", email: "", phone: "", password: "", unit_id: "" });
  const [savingUser, setSavingUser] = useState(false);
  const [accountUnit, setAccountUnit] = useState(null); // hesap işlemi yapılan daire

  // Raporlar
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportYear, setReportYear] = useState(new Date().getFullYear().toString());
  const [reportMonth, setReportMonth] = useState("");
  const [reportStatus, setReportStatus] = useState("");
  const [exporting, setExporting] = useState(null);

  // Daire yönetimi
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({
    unit_number: '', floor: '', block: '', m2_area: '',
    owner_name: '', owner_email: '', owner_phone: '',
    unit_type: '', weight_coefficient: '1.0', late_fee_exempt: false,
    dues_exempt: false, dues_exempt_reason: ''
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData || userData.role !== "apartment_manager") { navigate("/login"); return; }
    setUser(userData);
    loadData(userData);
  }, [navigate]);

  const loadData = async (userData) => {
    try {
      const u = userData || user;
      const [invoicesRes, unitsRes, overdueRes, tenantRes] = await Promise.all([
        api.get("/invoices"),
        api.get("/units"),
        api.get("/invoices/overdue-units"),
        api.get(`/tenants/${u.tenant_id}`)
      ]);
      // Sakin hesapları — daire eşleşmesini tabloda göstermek için
      api.get("/auth/users")
        .then(r => setResidentUsers(r.data.data || []))
        .catch(() => {});
      const invoicesData = invoicesRes.data.data;
      setInvoices(invoicesData);
      setUnits(unitsRes.data.data);
      setOverdueUnits(overdueRes.data.data);
      setTenant(tenantRes.data.data);
      const total = invoicesData.length;
      const paid = invoicesData.filter((inv) => inv.status === "paid").length;
      setStats({ total, paid, unpaid: total - paid });
    } catch (error) {
      console.error("Veri yükleme hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  // ── RAPORLAR ──
  const openReportsPage = () => {
    setCurrentPage("reports");
    loadReportData();
  };

  const loadReportData = async () => {
    setReportLoading(true);
    try {
      const res = await api.get("/reports/chart-data");
      setReportData(res.data.data);
    } catch (error) {
      console.error("Rapor verisi hatası:", error);
      alert("Rapor verisi yüklenemedi.");
    } finally {
      setReportLoading(false);
    }
  };

  const downloadReport = async (format) => {
    setExporting(format);
    try {
      const params = {};
      if (reportYear) params.year = reportYear;
      if (reportMonth) params.month = reportMonth;
      if (reportStatus) params.status = reportStatus;
      const res = await api.get(`/reports/${format}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `aidat-raporu-${reportYear || "tum"}${reportMonth ? "-" + reportMonth : ""}.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Rapor indirme hatası:", error);
      alert("Rapor indirilemedi.");
    } finally {
      setExporting(null);
    }
  };

  // Daire tipi seçince katsayı otomatik doldur
  const handleUnitTypeSelect = (typeName) => {
    const types = typeof tenant?.unit_types === "string"
      ? JSON.parse(tenant.unit_types)
      : (tenant?.unit_types || []);
    const found = types.find(t => t.name === typeName);
    setUnitForm(prev => ({
      ...prev,
      unit_type: typeName,
      weight_coefficient: found ? String(found.coefficient) : prev.weight_coefficient
    }));
  };

  // Daire formu
  const openAddUnit = () => {
    setEditingUnit(null);
    setUnitForm({ unit_number: '', floor: '', block: '', m2_area: '', owner_name: '', owner_email: '', owner_phone: '', unit_type: '', weight_coefficient: '1.0', late_fee_exempt: false, dues_exempt: false, dues_exempt_reason: '' });
    setShowUnitForm(true);
  };
  const openEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({ unit_number: unit.unit_number, floor: unit.floor || '', block: unit.block || '', m2_area: unit.m2_area || '', owner_name: unit.owner_name, owner_email: unit.owner_email || '', owner_phone: unit.owner_phone || '', unit_type: unit.unit_type || '', weight_coefficient: unit.weight_coefficient || '1.0', late_fee_exempt: unit.late_fee_exempt || false, dues_exempt: unit.dues_exempt || false, dues_exempt_reason: unit.dues_exempt_reason || '' });
    setShowUnitForm(true);
  };
  const handleUnitSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUnit) {
        await api.put(`/units/${editingUnit.id}`, unitForm);
      } else {
        await api.post('/units', { ...unitForm, tenant_id: user.tenant_id });
      }
      setShowUnitForm(false); setEditingUnit(null);
      loadData(user);
    } catch (error) { alert('Hata: ' + (error.response?.data?.message || 'İşlem başarısız')); }
  };
  const handleDeleteUnit = async (id, number) => {
    if (!window.confirm(`Daire ${number} silinecek. Emin misiniz?`)) return;
    try { await api.delete(`/units/${id}`); loadData(user); }
    catch (error) { alert('Hata: ' + (error.response?.data?.message || 'Silme başarısız')); }
  };

  // Muafiyet
  const handleExemptToggle = async (unit) => {
    setExemptLoading(true);
    try {
      await api.patch(`/units/${unit.id}/late-fee-exempt`, { late_fee_exempt: !unit.late_fee_exempt });
      setUnits(prev => prev.map(u => u.id === unit.id ? { ...u, late_fee_exempt: !u.late_fee_exempt } : u));
      loadData(user);
    } catch { alert("Muafiyet güncellenemedi."); } finally { setExemptLoading(false); }
  };

  const openMonthExemptModal = async () => {
    try { const res = await api.get(`/invoices/late-fees/exemptions?tenant_id=${user.tenant_id}`); setExemptions(res.data.data); } catch {}
    setExemptUnit(""); setExemptMonth(""); setExemptYear(new Date().getFullYear().toString()); setExemptNote("");
    setShowMonthExemptModal(true);
  };
  const handleAddExemption = async () => {
    if (!exemptMonth || !exemptYear) { alert("Ay ve yıl zorunlu"); return; }
    if (!exemptUnit) { alert("Daire veya 'Tüm Daireler' seçin"); return; }
    try {
      if (exemptUnit === "ALL") {
        await api.post("/invoices/late-fees/exemptions/bulk", { exempt_month: parseInt(exemptMonth), exempt_year: parseInt(exemptYear), note: exemptNote });
      } else {
        await api.post("/invoices/late-fees/exemptions", { unit_id: exemptUnit, exempt_month: parseInt(exemptMonth), exempt_year: parseInt(exemptYear), note: exemptNote });
      }
      const res = await api.get(`/invoices/late-fees/exemptions?tenant_id=${user.tenant_id}`);
      setExemptions(res.data.data);
      setExemptUnit(""); setExemptMonth(""); setExemptNote("");
      loadData(user);
    } catch (e) { alert(e.response?.data?.message || "Hata oluştu"); }
  };
  const handleDeleteExemption = async (id) => {
    if (!window.confirm("Muafiyeti silmek istiyor musunuz?")) return;
    try { await api.delete(`/invoices/late-fees/exemptions/${id}`); setExemptions(prev => prev.filter(e => e.id !== id)); loadData(user); }
    catch { alert("Silinemedi."); }
  };

  // Borç detayı
  const openDebtModal = async (unitId) => {
    setDebtLoading(true); setDebtDetail(null);
    setDebtDiscountValue(""); setDebtDiscountNote(""); setDebtDiscountTarget("unit");
    setShowDebtModal(true);
    try { const res = await api.get(`/invoices/unit-debt/${unitId}`); setDebtDetail(res.data.data); }
    catch { alert("Borç detayı yüklenemedi."); setShowDebtModal(false); } finally { setDebtLoading(false); }
  };
  const handleDebtDiscount = async () => {
    if (!debtDiscountValue) { alert("İndirim değeri girin"); return; }
    setApplyingDebtDiscount(true);
    try {
      if (debtDiscountTarget === "unit") {
        await api.post(`/invoices/unit-discount/${debtDetail.unit_id}`, { discount_type: debtDiscountType, discount_value: parseFloat(debtDiscountValue), note: debtDiscountNote });
      } else {
        await api.post(`/invoices/${debtDiscountTarget}/discount`, { discount_type: debtDiscountType, discount_value: parseFloat(debtDiscountValue), note: debtDiscountNote });
      }
      alert("İndirim uygulandı");
      setDebtDiscountValue(""); setDebtDiscountNote(""); setDebtDiscountTarget("unit");
      const res = await api.get(`/invoices/unit-debt/${debtDetail.unit_id}`);
      setDebtDetail(res.data.data);
      loadData(user);
    } catch (e) { alert(e.response?.data?.message || "Hata oluştu"); } finally { setApplyingDebtDiscount(false); }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 8) {
      setResetMessage({ type: "error", text: "Şifre en az 8 karakter olmalı" });
      return;
    }
    try {
      const res = await api.patch(`/auth/users/${userId}/reset-password`, { new_password: newPassword });
      setResetMessage({ type: "success", text: res.data.message });
      setResetUserId(null);
      setNewPassword("");
      setAccountUnit(null);
    } catch (e) {
      setResetMessage({ type: "error", text: e.response?.data?.message || "Şifre değiştirilemedi" });
    }
  };

  // Belirli bir daire için hesap açma penceresi
  const openAccountModal = (unit) => {
    const existing = residentUsers.find(u => u.unit_id === unit.id);
    setAccountUnit(unit);
    setResetMessage(null);
    setResetUserId(null);
    setNewPassword("");
    setNewUser({
      full_name: existing ? existing.full_name : (unit.owner_name || ""),
      email: existing ? existing.email : "",
      phone: existing ? (existing.phone || "") : (unit.owner_phone || ""),
      password: "",
      unit_id: unit.id,
    });
  };

  const handleCreateUser = async () => {
    if (!newUser.full_name.trim() || !newUser.email.trim()) {
      setResetMessage({ type: "error", text: "Ad soyad ve e-posta zorunlu" });
      return;
    }
    if (!newUser.password || newUser.password.length < 8) {
      setResetMessage({ type: "error", text: "Şifre en az 8 karakter olmalı" });
      return;
    }
    setSavingUser(true);
    try {
      await api.post("/auth/register", {
        full_name: newUser.full_name.trim(),
        email: newUser.email.trim(),
        phone: newUser.phone.trim() || null,
        password: newUser.password,
        role: "resident",
        unit_id: newUser.unit_id || null,
      });
      setResetMessage({ type: "success", text: `${newUser.full_name} hesabı oluşturuldu` });
      setNewUser({ full_name: "", email: "", phone: "", password: "", unit_id: "" });
      setAccountUnit(null);
      const res = await api.get("/auth/users");
      setResidentUsers(res.data.data || []);
    } catch (e) {
      setResetMessage({ type: "error", text: e.response?.data?.message || "Hesap oluşturulamadı" });
    } finally {
      setSavingUser(false);
    }
  };

  const handleLogout = () => { localStorage.clear(); navigate("/login"); };

  const openNotifModal = (unit) => {
    setNotifUnit(unit);
    setNotifType("payment_overdue");
    setNotifChannels(["sms", "email", "whatsapp"]);
    setNotifResult(null);
    setShowNotifModal(true);
  };

  const handleSendNotif = async () => {
    setSendingNotif(true); setNotifResult(null);
    try {
      const res = await api.post("/notifications/send", {
        unit_id: notifUnit.unit_id,
        type: notifType,
        channels: notifChannels
      });
      setNotifResult({ success: true, message: res.data.message, results: res.data.results });
    } catch (e) {
      setNotifResult({ success: false, message: e.response?.data?.message || "Hata oluştu" });
    } finally {
      setSendingNotif(false);
    }
  };

  const handleSendBulkNotif = async () => {
    if (!window.confirm("Tüm geciken dairelere bildirim gönderilecek. Devam?")) return;
    setSendingNotif(true);
    try {
      const res = await api.post("/notifications/send-bulk", {
        tenant_id: user.tenant_id,
        type: "payment_overdue",
        channels: ["sms", "email", "whatsapp"]
      });
      alert(res.data.message);
    } catch (e) {
      alert(e.response?.data?.message || "Hata oluştu");
    } finally {
      setSendingNotif(false);
    }
  };
    // ── KOTALI HATIRLATMA ──
  const openReminderModal = async (preselectUnitId = null) => {
    setShowReminderModal(true);
    setReminderResult(null);
    setLoadingReminders(true);
    setSelectedReminders([]);
    try {
      const res = await api.get(`/tenants/${user.tenant_id}/reminder-targets`);
      const list = res.data.data.targets || [];
      setReminderTargets(list);
      setReminderQuota(res.data.data.quota || 2);
      if (preselectUnitId) {
        const t = list.find(x => x.unit_id === preselectUnitId);
        if (t && t.gonderilebilir) setSelectedReminders([preselectUnitId]);
      } else {
        setSelectedReminders(list.filter(t => t.gonderilebilir).map(t => t.unit_id));
      }
    } catch (e) {
      setReminderResult({ error: e.response?.data?.message || "Liste yüklenemedi" });
    } finally {
      setLoadingReminders(false);
    }
  };

  const toggleReminder = (unitId) =>
    setSelectedReminders(prev =>
      prev.includes(unitId) ? prev.filter(id => id !== unitId) : [...prev, unitId]
    );

  const handleSendReminders = async () => {
    if (selectedReminders.length === 0) return;
    setSendingReminders(true);
    setReminderResult(null);
    try {
      const res = await api.post(`/tenants/${user.tenant_id}/send-reminders`, {
        unit_ids: selectedReminders
      });
      setReminderResult(res.data.data);
      await openReminderModalRefresh();
    } catch (e) {
      setReminderResult({ error: e.response?.data?.message || "Gönderim başarısız" });
    } finally {
      setSendingReminders(false);
    }
  };

  // Gönderim sonrası kalan hakları tazele (modalı kapatmadan)
  const openReminderModalRefresh = async () => {
    try {
      const res = await api.get(`/tenants/${user.tenant_id}/reminder-targets`);
      setReminderTargets(res.data.data.targets || []);
      setSelectedReminders([]);
    } catch {}
  };

  const openPayModal = (invoice) => {
    setPayingInvoice(invoice);
    setPayMethod("nakit");
    setPayNote("");
    setShowPayModal(true);
  };

  const handleMarkPaid = async () => {
    setMarkingPaid(true);
    try {
      await api.patch(`/invoices/${payingInvoice.id}/mark-paid`, {
        payment_method: payMethod,
        payment_note: payNote
      });
      setShowPayModal(false);
      loadData(user);
    } catch (e) {
      alert(e.response?.data?.message || "Hata oluştu");
    } finally {
      setMarkingPaid(false);
    }
  };
  const openInvoiceModal = () => {
    const d = new Date();
    setMonth((d.getMonth() + 1).toString()); setYear(d.getFullYear().toString()); setSelectedUnits([]); setInvoiceType("all"); setShowModal(true);
  };
  const handleUnitToggle = (unitId) => setSelectedUnits(prev => prev.includes(unitId) ? prev.filter(id => id !== unitId) : [...prev, unitId]);
  const handleCreateInvoices = async () => {
    if (!month || !year) { alert("Lütfen ay ve yıl seçin"); return; }
    if (invoiceType === "custom" && selectedUnits.length === 0) { alert("Lütfen en az bir daire seçin"); return; }
    try {
      if (invoiceType === "all") {
        await api.post("/invoices/generate", { tenant_id: user.tenant_id, month: parseInt(month), year: parseInt(year) });
      } else {
        for (const unitId of selectedUnits) {
          await api.post("/invoices/generate-single", { tenant_id: user.tenant_id, unit_id: unitId, month: parseInt(month), year: parseInt(year) });
        }
      }
      alert("Faturalar oluşturuldu"); setShowModal(false); loadData(user);
    } catch (e) { alert(e.response?.data?.message || "Hata oluştu"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-xl">Yükleniyor...</div></div>;
  if (currentPage === "settings") return <PaymentSettings tenantId={user?.tenant_id} onBack={() => setCurrentPage("dashboard")} />;

  const unitTypes = typeof tenant?.unit_types === "string"
    ? JSON.parse(tenant.unit_types)
    : (tenant?.unit_types || []);

  const filteredInvoices = invoices.filter(inv => {
    if (filterUnit && inv.unit_id !== filterUnit) return false;
    if (filterStatus && inv.status !== filterStatus) return false;
    return true;
  });

  const totalOverdueDebt = overdueUnits.reduce((s, u) => s + parseFloat(u.total_debt || 0), 0);
  const totalOverdueFee = overdueUnits.reduce((s, u) => s + parseFloat(u.total_fee || 0), 0);

  // ── RAPORLAR SAYFASI ──
  if (currentPage === "reports") {
    const STATUS_COLORS = ["#16a34a", "#ea580c", "#dc2626"];
    const pieData = reportData ? [
      { name: "Ödendi", value: reportData.statusCounts.paid },
      { name: "Bekliyor", value: reportData.statusCounts.waiting },
      { name: "Gecikmiş", value: reportData.statusCounts.overdue },
    ].filter(d => d.value > 0) : [];

    return (
      <div className="min-h-screen bg-gray-100">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button onClick={() => setCurrentPage("dashboard")} className="text-gray-500 hover:text-gray-700 text-sm">← Geri</button>
              <h1 className="text-xl font-bold">📊 Raporlar</h1>
            </div>
            <button onClick={loadReportData} disabled={reportLoading} className="text-sm text-blue-600 hover:underline disabled:opacity-50">↻ Yenile</button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-6">
          {/* EXPORT BAR */}
          <div className="bg-white rounded-xl shadow p-4 mb-6">
            <h3 className="font-bold text-gray-800 text-sm mb-3">📥 Rapor İndir</h3>
            <div className="flex gap-3 items-end flex-wrap">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Yıl</label>
                <select value={reportYear} onChange={(e) => setReportYear(e.target.value)} className="border rounded px-3 py-2 text-sm">
                  <option value="">Tüm Yıllar</option>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Ay</label>
                <select value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} className="border rounded px-3 py-2 text-sm">
                  <option value="">Tüm Aylar</option>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{MONTHS[m-1]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Durum</label>
                <select value={reportStatus} onChange={(e) => setReportStatus(e.target.value)} className="border rounded px-3 py-2 text-sm">
                  <option value="">Tümü</option>
                  <option value="paid">Ödendi</option>
                  <option value="unpaid">Ödenmemiş</option>
                </select>
              </div>
              <button
                onClick={() => downloadReport("excel")}
                disabled={exporting !== null}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg"
              >
                {exporting === "excel" ? "Hazırlanıyor..." : "📗 Excel İndir"}
              </button>
              <button
                onClick={() => downloadReport("pdf")}
                disabled={exporting !== null}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg"
              >
                {exporting === "pdf" ? "Hazırlanıyor..." : "📕 PDF İndir"}
              </button>
            </div>
          </div>

          {reportLoading && <div className="text-center text-gray-400 py-12">Grafikler yükleniyor...</div>}

          {!reportLoading && reportData && (
            <>
              {/* ÖZET KARTLARI */}
              <div className="grid grid-cols-3 gap-6 mb-6">
                <div className="bg-white p-6 rounded-xl shadow border-l-4 border-green-500">
                  <h3 className="text-gray-500 text-sm">Toplam Tahsilat</h3>
                  <p className="text-2xl font-bold text-green-600">{formatTL(reportData.summary.totalCollected)}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow border-l-4 border-orange-500">
                  <h3 className="text-gray-500 text-sm">Bekleyen Alacak</h3>
                  <p className="text-2xl font-bold text-orange-600">{formatTL(reportData.summary.totalPending)}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow border-l-4 border-red-500">
                  <h3 className="text-gray-500 text-sm">İşlemiş Faiz</h3>
                  <p className="text-2xl font-bold text-red-600">{formatTL(reportData.summary.totalFee)}</p>
                </div>
              </div>

              {/* AYLIK TREND */}
              <div className="bg-white rounded-xl shadow p-5 mb-6">
                <h3 className="font-bold text-gray-800 mb-4">📈 Aylık Tahsilat Trendi (Son 12 Ay)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reportData.monthly} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₺${v.toLocaleString("tr-TR")}`} width={80} />
                    <Tooltip formatter={(v) => formatTL(v)} />
                    <Legend />
                    <Bar dataKey="collected" name="Tahsil Edilen" fill="#16a34a" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="pending" name="Bekleyen" fill="#ea580c" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* DURUM DAĞILIMI */}
                <div className="bg-white rounded-xl shadow p-5">
                  <h3 className="font-bold text-gray-800 mb-4">🥧 Fatura Durum Dağılımı</h3>
                  {pieData.length === 0 ? (
                    <p className="text-gray-400 text-sm py-10 text-center">Henüz fatura yok.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name}: ${e.value}`}>
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={
                              entry.name === "Ödendi" ? STATUS_COLORS[0]
                              : entry.name === "Bekliyor" ? STATUS_COLORS[1]
                              : STATUS_COLORS[2]
                            } />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, n) => [`${v} fatura`, n]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* EN BORÇLU DAİRELER */}
                <div className="bg-white rounded-xl shadow p-5">
                  <h3 className="font-bold text-gray-800 mb-4">🚨 En Borçlu Daireler</h3>
                  {reportData.topDebtors.length === 0 ? (
                    <p className="text-green-600 text-sm py-10 text-center">✅ Gecikmiş borç yok, tüm daireler güncel.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={reportData.topDebtors} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `₺${v.toLocaleString("tr-TR")}`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                        <Tooltip formatter={(v) => formatTL(v)} />
                        <Bar dataKey="debt" name="Toplam Borç" fill="#dc2626" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── DAİRE YÖNETİMİ SAYFASI ──
  if (currentPage === "units") {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button onClick={() => { setCurrentPage("dashboard"); setShowUnitForm(false); }} className="text-gray-500 hover:text-gray-700 text-sm">← Geri</button>
              <div>
                <h1 className="text-xl font-bold">🏠 Daire Yönetimi</h1>
                {units.length > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {units.filter(u => residentUsers.some(r => r.unit_id === u.id)).length}/{units.length} dairenin sakin hesabı açık
                  </p>
                )}
              </div>
            </div>
            <button onClick={openAddUnit} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700">+ Daire Ekle</button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto p-6">
          {(() => {
            const missing = units.filter(u => !residentUsers.some(r => r.unit_id === u.id));
            return missing.length > 0 ? (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
                <p className="text-sm text-amber-800">
                  <strong>{missing.length} dairenin</strong> sakin hesabı yok. Bu dairelerdeki sakinler panele giriş yapıp
                  borçlarını göremez. Tablodaki <strong>"+ Hesap Aç"</strong> düğmesiyle oluşturabilirsiniz.
                </p>
              </div>
            ) : null;
          })()}
          {showUnitForm && (
            <div className="bg-white rounded-xl shadow p-6 mb-6">
              <h3 className="font-bold text-gray-800 mb-4">{editingUnit ? `Daire ${editingUnit.unit_number} Düzenle` : 'Yeni Daire Ekle'}</h3>
              <form onSubmit={handleUnitSubmit} className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Daire No *</label>
                    <input type="text" value={unitForm.unit_number} onChange={(e) => setUnitForm({...unitForm, unit_number: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Kat</label>
                    <input type="number" value={unitForm.floor} onChange={(e) => setUnitForm({...unitForm, floor: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Blok</label>
                    <input type="text" value={unitForm.block} onChange={(e) => setUnitForm({...unitForm, block: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" placeholder="A, B..." />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Mal Sahibi Adı *</label>
                  <input type="text" value={unitForm.owner_name} onChange={(e) => setUnitForm({...unitForm, owner_name: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">E-posta</label>
                    <input type="email" value={unitForm.owner_email} onChange={(e) => setUnitForm({...unitForm, owner_email: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Telefon</label>
                    <input type="text" value={unitForm.owner_phone} onChange={(e) => setUnitForm({...unitForm, owner_phone: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">m² Alan</label>
                    <input type="number" value={unitForm.m2_area} onChange={(e) => setUnitForm({...unitForm, m2_area: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">
                      Daire Tipi
                      {unitTypes.length === 0 && <span className="text-gray-400 ml-1 font-normal">(Aidat Ayarları'ndan ekleyin)</span>}
                    </label>
                    {unitTypes.length > 0 ? (
                      <select
                        value={unitForm.unit_type}
                        onChange={(e) => handleUnitTypeSelect(e.target.value)}
                        className="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="">— Manuel katsayı gir —</option>
                        {unitTypes.map((t, i) => (
                          <option key={i} value={t.name}>{t.name} (×{Number(t.coefficient).toFixed(2)})</option>
                        ))}
                      </select>
                    ) : (
                      <input type="text" value={unitForm.unit_type} onChange={(e) => setUnitForm({...unitForm, unit_type: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" placeholder="ör: 3+1, Dükkan..." />
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Aidat Katsayısı
                    <span className="text-gray-400 ml-1 font-normal">(1.0 = normal, 1.5 = %50 fazla)</span>
                  </label>
                  <input
                    type="number"
                    value={unitForm.weight_coefficient}
                    onChange={(e) => setUnitForm({...unitForm, weight_coefficient: e.target.value})}
                    className="w-48 border rounded px-3 py-2 text-sm"
                    step="0.05" min="0.1"
                  />
                  {unitForm.unit_type && unitTypes.find(t => t.name === unitForm.unit_type) && (
                    <span className="ml-2 text-xs text-blue-600">← {unitForm.unit_type} tipinden otomatik</span>
                  )}
                </div>
                <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg">
                  <input type="checkbox" id="unit_exempt_mgr" checked={unitForm.late_fee_exempt} onChange={(e) => setUnitForm({...unitForm, late_fee_exempt: e.target.checked})} className="w-4 h-4" />
                  <label htmlFor="unit_exempt_mgr" className="text-sm cursor-pointer">
                    <strong>Faiz muafiyeti</strong> — aidat borcu var, gecikme faizi işlemez
                  </label>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="unit_dues_exempt_mgr"
                      checked={unitForm.dues_exempt}
                      onChange={(e) => setUnitForm({...unitForm, dues_exempt: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="unit_dues_exempt_mgr" className="text-sm cursor-pointer">
                      <strong>Aidat muafiyeti</strong> — hiç aidat ödemez (kapıcı vb.)
                    </label>
                  </div>
                  {unitForm.dues_exempt && (
                    <div>
                      <input
                        type="text"
                        value={unitForm.dues_exempt_reason}
                        onChange={(e) => setUnitForm({...unitForm, dues_exempt_reason: e.target.value})}
                        placeholder="Gerekçe (zorunlu) — ör: Kapıcı dairesi, 2025 GK kararı"
                        className="w-full border rounded px-3 py-2 text-sm"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Bu daireye bundan sonra ₺0 fatura kesilir, borcu oluşmaz.
                        Gerekçe kayıt altına alınır (KMK md. 20).
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => { setShowUnitForm(false); setEditingUnit(null); }} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">İptal</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">{editingUnit ? 'Güncelle' : 'Ekle'}</button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">No</th>
                  <th className="px-4 py-3 text-left">Kat/Blok</th>
                  <th className="px-4 py-3 text-left">Mal Sahibi</th>
                  <th className="px-4 py-3 text-left">Telefon</th>
                  <th className="px-4 py-3 text-left">Tip</th>
                  <th className="px-4 py-3 text-left">Katsayı</th>
                  <th className="px-4 py-3 text-left">Muafiyet</th>
                  <th className="px-4 py-3 text-left">Sakin Hesabı</th>
                  <th className="px-4 py-3 text-left">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit, idx) => (
                  <tr key={unit.id} className={`border-t ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-4 py-3 font-semibold">{unit.unit_number}</td>
                    <td className="px-4 py-3 text-gray-500">{unit.floor ? `Kat ${unit.floor}` : '—'}{unit.block ? ` / ${unit.block}` : ''}</td>
                    <td className="px-4 py-3">{unit.owner_name}</td>
                    <td className="px-4 py-3 text-gray-500">{unit.owner_phone || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{unit.unit_type || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${parseFloat(unit.weight_coefficient) !== 1.0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                        ×{parseFloat(unit.weight_coefficient).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 items-start">
                        {unit.dues_exempt && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full" title={unit.dues_exempt_reason || ''}>🛡️ Aidat muaf</span>}
                        {unit.late_fee_exempt && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Faiz muaf</span>}
                        {!unit.dues_exempt && !unit.late_fee_exempt && <span className="text-gray-300 text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const acc = residentUsers.find(u => u.unit_id === unit.id);
                        return acc ? (
                          <button onClick={() => openAccountModal(unit)} className="text-left group">
                            <span className="text-xs text-green-700 font-medium">✅ Hesap var</span>
                            <span className="block text-[11px] text-gray-400 truncate max-w-[150px] group-hover:text-blue-600">{acc.email}</span>
                          </button>
                        ) : (
                          <button onClick={() => openAccountModal(unit)} className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 px-2.5 py-1 rounded-lg font-medium">
                            + Hesap Aç
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEditUnit(unit)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Düzenle</button>
                        <button onClick={() => handleDeleteUnit(unit.id, unit.unit_number)} className="text-red-500 hover:text-red-700 text-xs font-medium">Sil</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {units.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Henüz daire eklenmemiş.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ── DASHBOARD ──
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Site Yönetim Paneli</h1>
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-sm text-gray-600 mr-1 cursor-pointer hover:underline" onClick={() => setShowProfileModal(true)}>{user?.full_name}</span>
            <button onClick={openReportsPage} className="bg-green-100 hover:bg-green-200 text-green-800 text-sm font-semibold px-3 py-2 rounded-xl">📊 Raporlar</button>
            <button onClick={() => setCurrentPage("units")} className="bg-blue-100 hover:bg-blue-200 text-blue-800 text-sm font-semibold px-3 py-2 rounded-xl">🏠 Daireler</button>
            <button onClick={openMonthExemptModal} className="bg-purple-100 hover:bg-purple-200 text-purple-800 text-sm font-semibold px-3 py-2 rounded-xl">📅 Ay Muafiyeti</button>
            <button onClick={() => setShowExemptModal(true)} className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-sm font-semibold px-3 py-2 rounded-xl">🚫 Kalıcı Muafiyet</button>
            <button onClick={() => setCurrentPage("settings")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold px-3 py-2 rounded-xl">⚙️ Aidat Ayarları</button>
            <button onClick={handleLogout} className="bg-red-500 text-white px-3 py-2 rounded">Çıkış</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* STATS */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-6 rounded shadow"><h3 className="text-gray-500 text-sm">Toplam Fatura</h3><p className="text-3xl font-bold text-blue-600">{stats.total}</p></div>
          <div className="bg-white p-6 rounded shadow"><h3 className="text-gray-500 text-sm">Ödenen</h3><p className="text-3xl font-bold text-green-600">{stats.paid}</p></div>
          <div className="bg-white p-6 rounded shadow"><h3 className="text-gray-500 text-sm">Bekleyen</h3><p className="text-3xl font-bold text-orange-600">{stats.unpaid}</p></div>
        </div>

        {/* FAİZ DURUM KARTI — sadece bilgi, ayar PaymentSettings'te */}
        {tenant && (
          <div className={`mb-6 p-4 rounded-xl border flex items-center justify-between ${tenant.late_fee_enabled ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="text-sm">
              <span className="font-semibold text-gray-700">💰 Gecikme Faizi: </span>
              {tenant.late_fee_enabled
                ? <>
                    <span className="text-orange-700 font-semibold">
                      Aktif — Aylık %{Math.min(parseFloat(tenant.late_fee_rate) || 0, parseFloat(tenant.max_late_fee_rate) || 5)} (basit usul)
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Üst sınırınız: <strong>%{parseFloat(tenant.max_late_fee_rate) || 5}</strong>
                      {(parseFloat(tenant.max_late_fee_rate) || 5) > 5
                        ? " (özel izin tanımlı)"
                        : " — Kat Mülkiyeti Kanunu md. 20 yasal sınırı. Daha yükseği için bizimle iletişime geçin."}
                    </p>
                  </>
                : <span className="text-gray-500">Pasif</span>}
            </div>
            <button onClick={() => setCurrentPage("settings")} className="text-xs text-orange-600 hover:underline whitespace-nowrap">Ayarla →</button>
          </div>
        )}

        {/* GECİKEN DAİRELER */}
        {overdueUnits.length > 0 && (
          <div className="mb-6 bg-white rounded-xl shadow overflow-hidden">
            <div className="px-5 py-4 bg-red-50 border-b border-red-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-red-800">🚨 Geciken Daireler</h3>
                  <p className="text-sm text-red-600 mt-0.5">
                    {overdueUnits.length} daire — Toplam borç: <strong>₺{totalOverdueDebt.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</strong>
                    {totalOverdueFee > 0 && <span className="text-orange-600 ml-2">(Faiz: ₺{totalOverdueFee.toLocaleString("tr-TR", { minimumFractionDigits: 2 })})</span>}
                  </p>
                </div>
                <button
                  onClick={() => openReminderModal()}
                  className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-4 py-2 rounded-xl"
                >
                  📣 Hatırlatma Gönder
                </button>
              </div>
            </div>
            <div className="divide-y">
              {overdueUnits.map((unit) => (
                <div key={unit.unit_id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => openDebtModal(unit.unit_id)}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800">Daire {unit.unit_number}</span>
                      <span className="text-gray-500 text-sm">— {unit.owner_name}</span>
                      {unit.owner_phone && <span className="text-gray-400 text-xs">{unit.owner_phone}</span>}
                      {unit.exempt_this_month && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">📅 Bu ay faiz muaf</span>}
                      {unit.late_fee_exempt && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Faizden kalıcı muaf</span>}
                    </div>
                    <div className="flex gap-4 mt-0.5 text-sm flex-wrap">
                      <span className="text-gray-500">{unit.invoice_count} fatura</span>
                      <span className="text-gray-600">Aidat: ₺{parseFloat(unit.total_base).toFixed(2)}</span>
                      {parseFloat(unit.total_fee) > 0 && <span className="text-orange-600">+ Faiz: ₺{parseFloat(unit.total_fee).toFixed(2)}</span>}
                      <span className="font-bold text-red-700">Toplam: ₺{parseFloat(unit.total_debt).toFixed(2)} <span className="text-xs font-normal text-gray-400">(aidat + faiz)</span></span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); openReminderModal(unit.unit_id); }} className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-2 py-1 rounded">
                      📣 Bildir
                    </button>
                    <span className="text-blue-500 text-sm self-center">Detay →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {overdueUnits.length === 0 && !loading && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm font-semibold">✅ Geciken fatura yok, tüm daireler güncel.</div>
        )}

        {/* FATURA OLUŞTUR + FİLTRE */}
        <div className="flex gap-3 mb-4 items-center flex-wrap">
          <button onClick={openInvoiceModal} className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold">+ Yeni Fatura Oluştur</button>
          <select value={filterUnit} onChange={(e) => setFilterUnit(e.target.value)} className="border rounded px-3 py-2 text-sm">
            <option value="">Tüm Daireler</option>
            {units.map(u => <option key={u.id} value={u.id}>Daire {u.unit_number} - {u.owner_name}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border rounded px-3 py-2 text-sm">
            <option value="">Tüm Durumlar</option>
            <option value="unpaid">Ödenmemiş</option>
            <option value="paid">Ödendi</option>
          </select>
          {(filterUnit || filterStatus) && <button onClick={() => { setFilterUnit(""); setFilterStatus(""); }} className="text-sm text-gray-500 hover:text-gray-700 underline">Filtreyi Temizle</button>}
          <span className="text-sm text-gray-400 ml-auto">{filteredInvoices.length} fatura</span>
        </div>

        {/* FATURA TABLOSU */}
        <div className="bg-white rounded shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm">Fatura No</th>
                <th className="px-4 py-3 text-left text-sm">Daire</th>
                <th className="px-4 py-3 text-left text-sm">Mal Sahibi</th>
                <th className="px-4 py-3 text-left text-sm">Dönem</th>
                <th className="px-4 py-3 text-left text-sm">Aidat</th>
                <th className="px-4 py-3 text-left text-sm">İndirim</th>
                <th className="px-4 py-3 text-left text-sm">Durum</th>
                <th className="px-4 py-3 text-left text-sm">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className={`border-t hover:bg-gray-50 ${invoice.status === 'unpaid' && new Date(invoice.due_date) < new Date() ? 'bg-red-50' : ''}`}>
                  <td className="px-4 py-3 text-sm">{invoice.invoice_number}</td>
                  <td className="px-4 py-3 text-sm font-medium">{invoice.unit_number}</td>
                  <td className="px-4 py-3 text-sm">{invoice.owner_name}</td>
                  <td className="px-4 py-3 text-sm">
                    {invoice.period_month}/{invoice.period_year}
                    {invoice.is_period_exempt && <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">📅 Faiz muaf</span>}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold">{parseFloat(invoice.base_amount).toFixed(2)} ₺</td>
                  <td className="px-4 py-3 text-sm">
                    {invoice.discount_amount && parseFloat(invoice.discount_amount) > 0
                      ? <span className="text-green-600 font-semibold">-{parseFloat(invoice.discount_amount).toFixed(2)} ₺</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {invoice.status === "paid"
                      ? <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Ödendi</span>
                      : new Date(invoice.due_date) < new Date()
                        ? <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">Gecikmiş {parseInt(invoice.months_overdue) > 0 ? `(${invoice.months_overdue} ay)` : ""}</span>
                        : <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">Bekliyor</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {invoice.status !== "paid" && (
                      <button
                        onClick={() => openPayModal(invoice)}
                        className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-2 py-1 rounded font-medium"
                      >
                        ✓ Ödendi İşaretle
                      </button>
                    )}
                    {invoice.status === "paid" && (
                      invoice.is_dues_exempt
                        ? <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">🛡️ Muaf</span>
                        : <button
                            onClick={() => setPaidDetail(invoice)}
                            className="text-xs text-gray-600 hover:text-blue-600 hover:underline text-left"
                          >
                            {paymentMethodLabel(invoice.payment_method)}
                            {invoice.payment_note && <span className="ml-1">📝</span>}
                          </button>
                    )}
                  </td>
                </tr>
              ))}
              {filteredInvoices.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">Fatura bulunamadı.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {/* KOTALI HATIRLATMA MODAL */}
      <Modal show={showReminderModal} onClose={() => setShowReminderModal(false)} title="📣 Ödeme Hatırlatması Gönder" maxWidth="max-w-2xl">
        {loadingReminders && <p className="text-gray-400 text-sm py-6 text-center">Yükleniyor...</p>}

        {!loadingReminders && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4 text-sm text-blue-800">
              Seçili dairelere <strong>SMS ve e-posta</strong> ile ödeme linki gönderilir.
              Her daire ayda en fazla <strong>{reminderQuota} kez</strong> hatırlatma alabilir.
            </div>

            {reminderResult?.error && (
              <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                ❌ {reminderResult.error}
              </div>
            )}

            {reminderResult && !reminderResult.error && (
              <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
                <p className="font-semibold mb-1">
                  ✅ {reminderResult.sent} gönderildi
                  {reminderResult.skipped > 0 && ` · ${reminderResult.skipped} atlandı`}
                  {reminderResult.failed > 0 && ` · ${reminderResult.failed} hata`}
                </p>
                {reminderResult.details?.length > 0 && (
                  <div className="text-xs space-y-0.5 mt-2">
                    {reminderResult.details.map((d, i) => (
                      <div key={i}>
                        Daire {d.unit}: {
                          d.sonuc === "gonderildi" ? "gönderildi"
                          : d.sonuc === "kota_doldu" ? "aylık hakkı dolmuş"
                          : d.sonuc === "zaten_gitti" ? "zaten gönderilmiş"
                          : "gönderilemedi"
                        }
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {reminderTargets.length === 0 ? (
              <p className="text-gray-400 text-sm py-6 text-center">Borcu olan daire yok.</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">{selectedReminders.length} daire seçili</span>
                  <div className="flex gap-3 text-xs">
                    <button
                      onClick={() => setSelectedReminders(reminderTargets.filter(t => t.gonderilebilir).map(t => t.unit_id))}
                      className="text-blue-600 hover:underline"
                    >
                      Tümünü seç
                    </button>
                    <button onClick={() => setSelectedReminders([])} className="text-gray-500 hover:underline">
                      Temizle
                    </button>
                  </div>
                </div>

                <div className="border rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                  {reminderTargets.map((t, idx) => (
                    <label
                      key={t.unit_id}
                      className={`flex items-center gap-3 px-4 py-3 border-t first:border-t-0 ${
                        t.gonderilebilir ? "cursor-pointer hover:bg-gray-50" : "bg-gray-50 cursor-not-allowed opacity-60"
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={!t.gonderilebilir}
                        checked={selectedReminders.includes(t.unit_id)}
                        onChange={() => toggleReminder(t.unit_id)}
                        className="w-4 h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-800 text-sm">Daire {t.unit_number}</span>
                          <span className="text-gray-500 text-sm truncate">— {t.owner_name}</span>
                          {!t.owner_phone && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">telefon yok</span>}
                          {t.sms_opt_out && <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">SMS istemiyor</span>}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Borç: <strong className="text-red-600">{formatTL(t.borc)}</strong>
                          <span className="ml-2">· {t.fatura_sayisi} fatura</span>
                        </p>
                      </div>
                      <span className={`text-xs font-semibold whitespace-nowrap ${t.kalan_hak > 0 ? "text-green-600" : "text-gray-400"}`}>
                        {t.kalan_hak > 0 ? `${t.kalan_hak} hak` : "hak doldu"}
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}

            <div className="flex gap-3 justify-end pt-4">
              <button onClick={() => setShowReminderModal(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">
                Kapat
              </button>
              <button
                onClick={handleSendReminders}
                disabled={sendingReminders || selectedReminders.length === 0}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white rounded-lg font-semibold"
              >
                {sendingReminders ? "Gönderiliyor..." : `Gönder (${selectedReminders.length})`}
              </button>
            </div>
          </>
        )}
      </Modal>
      {/* BORÇ DETAYI MODAL */}
      <Modal show={showDebtModal} onClose={() => setShowDebtModal(false)} title="📊 Daire Borç Detayı" maxWidth="max-w-3xl">
        {debtLoading && <p className="text-gray-400 text-sm">Yükleniyor...</p>}
        {debtDetail && (
          <>
            <div className="mb-4">
              <h3 className="font-bold text-gray-800 text-lg">Daire {debtDetail.unit_number} — {debtDetail.owner_name}</h3>
              {debtDetail.fee_enabled && !debtDetail.is_permanent_exempt && <p className="text-sm text-gray-500">Aylık gecikme faizi: <strong>%{debtDetail.fee_rate}</strong> (basit usul)</p>}
              {debtDetail.is_permanent_exempt && <p className="text-sm text-orange-600 font-semibold">⚠️ Faizden kalıcı muaf</p>}
              {!debtDetail.fee_enabled && <p className="text-sm text-gray-400">Gecikme faizi aktif değil</p>}
            </div>
            <div className="border rounded overflow-hidden mb-4">
              <div className="bg-gray-50 px-3 py-2 grid grid-cols-7 gap-1 text-xs font-semibold text-gray-500 uppercase">
                <span>Dönem</span><span>Kaç Ay</span><span>Aidat</span><span>İndirim</span><span>Net</span><span>Faiz</span><span>Toplam</span>
              </div>
              {debtDetail.items.length === 0 && <p className="text-sm text-gray-400 px-4 py-3">Gecikmiş fatura yok.</p>}
              {debtDetail.items.map((item) => (
                <div key={item.invoice_id} className={`px-3 py-2.5 border-t grid grid-cols-7 gap-1 text-sm items-center ${item.is_period_exempt ? 'bg-purple-50' : ''}`}>
                  <span className="font-medium text-gray-700">
                    {MONTHS[item.period_month - 1]} {item.period_year}
                    {item.is_period_exempt && <span className="block text-xs text-purple-600">📅 {item.exempt_reason === 'permanent' ? 'Kalıcı muaf' : 'Ay muaf'}</span>}
                  </span>
                  <span className={item.months_overdue > 0 ? "text-red-600 font-semibold" : "text-gray-400"}>{item.months_overdue > 0 ? `${item.months_overdue} ay` : "—"}</span>
                  <span className="text-gray-600">₺{parseFloat(item.base_amount).toFixed(2)}</span>
                  <span className={parseFloat(item.discount) > 0 ? "text-green-600" : "text-gray-300"}>{parseFloat(item.discount) > 0 ? `-₺${parseFloat(item.discount).toFixed(2)}` : "—"}</span>
                  <span className="text-gray-700">₺{parseFloat(item.effective_base).toFixed(2)}</span>
                  <span>
                    {item.is_period_exempt
                      ? (parseFloat(item.exempted_fee) > 0 ? <span className="line-through text-gray-400 text-xs" title="Muafiyet nedeniyle uygulanmadı">₺{parseFloat(item.exempted_fee).toFixed(2)}</span> : <span className="text-gray-300">—</span>)
                      : (parseFloat(item.fee_amount) > 0 ? <span className="text-orange-600 font-semibold">+₺{parseFloat(item.fee_amount).toFixed(2)}</span> : <span className="text-gray-300">—</span>)}
                  </span>
                  <span className="font-bold text-gray-800">₺{parseFloat(item.total_amount).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 mb-4">
              <div className="flex justify-between text-sm text-gray-600"><span>Toplam Aidat Borcu</span><span className="font-semibold">₺{parseFloat(debtDetail.summary.total_base).toFixed(2)}</span></div>
              {parseFloat(debtDetail.summary.total_fee) > 0 && <div className="flex justify-between text-sm text-orange-600"><span>Toplam Gecikme Faizi</span><span className="font-semibold">+₺{parseFloat(debtDetail.summary.total_fee).toFixed(2)}</span></div>}
              {parseFloat(debtDetail.summary.total_exempted_fee) > 0 && <div className="flex justify-between text-sm text-purple-500"><span>📅 Muafiyet nedeniyle düşülen faiz</span><span className="font-semibold line-through">₺{parseFloat(debtDetail.summary.total_exempted_fee).toFixed(2)}</span></div>}
              <div className="flex justify-between text-base font-bold text-gray-900 border-t pt-2"><span>Ödenecek Toplam</span><span className="text-red-700">₺{parseFloat(debtDetail.summary.total_debt).toFixed(2)}</span></div>
              {(parseFloat(debtDetail.summary.total_fee) > 0 || parseFloat(debtDetail.summary.total_exempted_fee) > 0) && (
                <p className="text-xs text-gray-400 text-right">
                  Aidat ₺{parseFloat(debtDetail.summary.total_base).toFixed(2)}
                  {parseFloat(debtDetail.summary.total_fee) > 0 && ` + Faiz ₺${parseFloat(debtDetail.summary.total_fee).toFixed(2)}`}
                  {parseFloat(debtDetail.summary.total_exempted_fee) > 0 && ` (muaf: ₺${parseFloat(debtDetail.summary.total_exempted_fee).toFixed(2)} uygulanmadı)`}
                </p>
              )}
            </div>
            {debtDetail.items.length > 0 && (
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-green-50 px-4 py-3 border-b"><h4 className="font-semibold text-green-800 text-sm">💸 İndirim Uygula</h4></div>
                <div className="px-4 py-3 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Neye Uygulanacak?</label>
                    <select value={debtDiscountTarget} onChange={(e) => setDebtDiscountTarget(e.target.value)} className="w-full border rounded px-3 py-1.5 text-sm">
                      <option value="unit">Tüm ödenmemiş faturalara (orantılı)</option>
                      {debtDetail.items.map(item => <option key={item.invoice_id} value={item.invoice_id}>Sadece {MONTHS[item.period_month - 1]} {item.period_year} (₺{parseFloat(item.total_amount).toFixed(2)})</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Tip</label>
                      <div className="flex gap-3">
                        <label className="flex items-center gap-1.5 text-sm cursor-pointer"><input type="radio" checked={debtDiscountType === "percent"} onChange={() => setDebtDiscountType("percent")} />% Yüzde</label>
                        <label className="flex items-center gap-1.5 text-sm cursor-pointer"><input type="radio" checked={debtDiscountType === "amount"} onChange={() => setDebtDiscountType("amount")} />₺ Tutar</label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{debtDiscountType === "percent" ? "Yüzde (%)" : "Tutar (₺)"}</label>
                      <input type="number" value={debtDiscountValue} onChange={(e) => setDebtDiscountValue(e.target.value)} placeholder={debtDiscountType === "percent" ? "ör: 10" : "ör: 100"} className="w-full border rounded px-3 py-1.5 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Not (opsiyonel)</label>
                    <input type="text" value={debtDiscountNote} onChange={(e) => setDebtDiscountNote(e.target.value)} placeholder="ör: Uzlaşma indirimi" className="w-full border rounded px-3 py-1.5 text-sm" />
                  </div>
                  <button onClick={handleDebtDiscount} disabled={applyingDebtDiscount} className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold py-2 rounded-lg">
                    {applyingDebtDiscount ? "Uygulanıyor..." : "İndirim Uygula"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal>

      <ProfileModal
        show={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        onUpdate={(updatedUser) => setUser(prev => ({ ...prev, ...updatedUser }))}
      />

      {/* BİLDİRİM MODAL */}
      <Modal show={showNotifModal} onClose={() => setShowNotifModal(false)} title="📣 Bildirim Gönder">
        {notifUnit && (
          <>
            <p className="text-sm text-gray-500 mb-4">
              Daire {notifUnit.unit_number} — {notifUnit.owner_name}
              {notifUnit.owner_phone && <span className="ml-2 text-gray-400">{notifUnit.owner_phone}</span>}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Bildirim Türü</label>
              <div className="space-y-2">
                {[
                  { value: "payment_overdue", label: "⚠️ Gecikmiş ödeme uyarısı" },
                  { value: "payment_reminder", label: "⏰ Ödeme hatırlatması" },
                  { value: "invoice_created", label: "📄 Yeni fatura bildirimi" },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                    <input type="radio" checked={notifType === opt.value} onChange={() => setNotifType(opt.value)} />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Kanallar</label>
              <div className="flex gap-3">
                {[
                  { value: "sms", label: "📱 SMS" },
                  { value: "email", label: "📧 E-posta" },
                  { value: "whatsapp", label: "💬 WhatsApp" },
                ].map(ch => (
                  <label key={ch.value} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifChannels.includes(ch.value)}
                      onChange={() => setNotifChannels(prev =>
                        prev.includes(ch.value) ? prev.filter(c => c !== ch.value) : [...prev, ch.value]
                      )}
                    />
                    <span className="text-sm">{ch.label}</span>
                  </label>
                ))}
              </div>
            </div>
            {notifResult && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${notifResult.success ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                {notifResult.message}
                {notifResult.results && (
                  <div className="mt-2 space-y-1 text-xs">
                    {Object.entries(notifResult.results).map(([ch, r]) => (
                      <div key={ch}>{ch}: {r?.mock ? "✅ Mock (gerçek hesap bağlanınca gönderilecek)" : r?.success ? "✅ Gönderildi" : "❌ Hata"}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowNotifModal(false)} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50">Kapat</button>
              <button
                onClick={handleSendNotif}
                disabled={sendingNotif || notifChannels.length === 0}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded font-semibold"
              >
                {sendingNotif ? "Gönderiliyor..." : "Gönder"}
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* DAİRE SAKİN HESABI MODAL */}
      <Modal show={!!accountUnit} onClose={() => { setAccountUnit(null); setResetMessage(null); }} title={accountUnit ? `👤 Daire ${accountUnit.unit_number} — Sakin Hesabı` : ""} maxWidth="max-w-lg">
        {accountUnit && (() => {
          const existing = residentUsers.find(u => u.unit_id === accountUnit.id);
          return (
            <>
              {resetMessage && (
                <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${resetMessage.type === "success" ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
                  {resetMessage.type === "success" ? "✅ " : "❌ "}{resetMessage.text}
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm">
                <p className="font-semibold text-gray-800">Daire {accountUnit.unit_number}</p>
                <p className="text-gray-500 text-xs mt-0.5">
                  {accountUnit.owner_name || "—"}{accountUnit.owner_phone ? ` · ${accountUnit.owner_phone}` : ""}
                </p>
              </div>

              {existing ? (
                <>
                  <div className="border rounded-xl p-4 mb-4">
                    <p className="text-xs text-gray-400 mb-1">Mevcut Hesap</p>
                    <p className="font-semibold text-gray-800">{existing.full_name}</p>
                    <p className="text-sm text-gray-600">{existing.email}</p>
                    {existing.phone && <p className="text-sm text-gray-500">{existing.phone}</p>}
                    <p className="text-xs text-gray-400 mt-1">
                      {existing.last_login ? `Son giriş: ${new Date(existing.last_login).toLocaleDateString("tr-TR")}` : "Henüz giriş yapmamış"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">🔑 Şifre Değiştir</p>
                    <div className="flex gap-2 flex-wrap">
                      <input
                        type="text"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Yeni şifre (en az 8 karakter)"
                        className="flex-1 min-w-[180px] border rounded-lg px-3 py-2 text-sm"
                      />
                      <button onClick={() => handleResetPassword(existing.id)} className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">
                        Kaydet
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Mevcut şifre görüntülenemez. Yeni şifreyi sakine kendiniz iletmelisiniz.</p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                    Bu dairenin hesabı yok. Oluşturmadan sakin panele giriş yapamaz.
                  </p>
                  <div className="space-y-2">
                    <input type="text" value={newUser.full_name} onChange={(e) => setNewUser({...newUser, full_name: e.target.value})} placeholder="Ad Soyad *" className="w-full border rounded-lg px-3 py-2 text-sm" />
                    <input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="E-posta *" className="w-full border rounded-lg px-3 py-2 text-sm" />
                    <input type="text" value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} placeholder="Telefon" className="w-full border rounded-lg px-3 py-2 text-sm" />
                    <input type="text" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Şifre (en az 8 karakter) *" className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <button onClick={handleCreateUser} disabled={savingUser} className="w-full mt-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-lg">
                    {savingUser ? "Oluşturuluyor..." : "Hesabı Oluştur"}
                  </button>
                  <p className="text-xs text-gray-400 mt-2">Ad ve telefon daire bilgisinden dolduruldu. Şifreyi sakine kendiniz iletmelisiniz.</p>
                </>
              )}
            </>
          );
        })()}
      </Modal>

      {/* ÖDEME DETAY MODAL */}
      <Modal show={!!paidDetail} onClose={() => setPaidDetail(null)} title="💳 Ödeme Detayı">
        {paidDetail && (
          <>
            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-1">
              <p className="text-sm text-gray-600">Fatura: <strong>{paidDetail.invoice_number}</strong></p>
              <p className="text-sm text-gray-600">Daire {paidDetail.unit_number} — {paidDetail.owner_name}</p>
              <p className="text-sm text-gray-600">Dönem: <strong>{paidDetail.period_month}/{paidDetail.period_year}</strong></p>
              <p className="text-sm font-bold text-gray-800">Tutar: {formatTL(paidDetail.total_amount)}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="border rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Ödeme Yöntemi</p>
                <p className="text-sm font-semibold">{paymentMethodLabel(paidDetail.payment_method)}</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Ödeme Tarihi</p>
                <p className="text-sm font-semibold">
                  {paidDetail.paid_at
                    ? new Date(paidDetail.paid_at).toLocaleString("tr-TR", {
                        day: "2-digit", month: "2-digit", year: "numeric",
                        hour: "2-digit", minute: "2-digit"
                      })
                    : "—"}
                </p>
              </div>
            </div>

            <div className="mb-5">
              <p className="text-xs text-gray-400 mb-1">Not</p>
              {paidDetail.payment_note
                ? <p className="text-sm bg-yellow-50 border border-yellow-200 rounded-lg p-3 whitespace-pre-wrap break-words">
                    {paidDetail.payment_note}
                  </p>
                : <p className="text-sm text-gray-300 italic">Not girilmemiş</p>}
            </div>

            <div className="flex justify-end">
              <button onClick={() => setPaidDetail(null)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">
                Kapat
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* MANUEL ÖDEME MODAL */}
      <Modal show={showPayModal} onClose={() => setShowPayModal(false)} title="✓ Ödendi Olarak İşaretle">
        {payingInvoice && (
          <>
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-600">Fatura: <strong>{payingInvoice.invoice_number}</strong></p>
              <p className="text-sm text-gray-600">Daire {payingInvoice.unit_number} — {payingInvoice.owner_name}</p>
              <p className="text-sm text-gray-600">Dönem: <strong>{payingInvoice.period_month}/{payingInvoice.period_year}</strong></p>
              <p className="text-sm font-bold text-gray-800 mt-1">Tutar: ₺{parseFloat(payingInvoice.total_amount).toFixed(2)}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Ödeme Yöntemi</label>
              <div className="grid grid-cols-3 gap-2">
                {PAYMENT_METHODS.map(m => (
                  <button
                    key={m.value}
                    onClick={() => setPayMethod(m.value)}
                    className={`py-2 px-3 rounded-lg text-sm font-medium border ${payMethod === m.value ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-sm font-medium mb-1">Not (opsiyonel)</label>
              <input
                type="text"
                value={payNote}
                onChange={e => setPayNote(e.target.value)}
                placeholder="ör: Kapıda nakit teslim alındı"
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowPayModal(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">İptal</button>
              <button
                onClick={handleMarkPaid}
                disabled={markingPaid}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg font-semibold"
              >
                {markingPaid ? "İşleniyor..." : "✓ Ödendi Olarak İşaretle"}
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* FATURA OLUŞTURMA MODAL */}
      <Modal show={showModal} onClose={() => setShowModal(false)} title="Yeni Fatura Oluştur">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><label className="block text-sm font-medium mb-1">Ay</label>
            <select value={month} onChange={(e) => setMonth(e.target.value)} className="w-full border rounded px-3 py-2">
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{m}. Ay</option>)}
            </select>
          </div>
          <div><label className="block text-sm font-medium mb-1">Yıl</label>
            <input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-full border rounded px-3 py-2" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Fatura Tipi</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={invoiceType === "all"} onChange={() => setInvoiceType("all")} />Tüm Daireler</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={invoiceType === "custom"} onChange={() => setInvoiceType("custom")} />Belirli Daireler</label>
          </div>
        </div>
        {invoiceType === "custom" && (
          <div className="mb-4 max-h-48 overflow-y-auto border rounded p-3">
            {units.map(unit => (
              <label key={unit.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={selectedUnits.includes(unit.id)} onChange={() => handleUnitToggle(unit.id)} />
                <span>Daire {unit.unit_number} - {unit.owner_name}</span>
              </label>
            ))}
          </div>
        )}
        <div className="flex gap-3 justify-end pt-2">
          <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50">İptal</button>
          <button onClick={handleCreateInvoices} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Oluştur</button>
        </div>
      </Modal>

      {/* KALICI MUAFİYET MODAL */}
      <Modal show={showExemptModal} onClose={() => setShowExemptModal(false)} title="🚫 Faizden Kalıcı Muafiyet">
        <p className="text-sm text-gray-500 mb-4">Muaf daireler hiçbir zaman gecikme faizine dahil edilmez.</p>
        <div className="border rounded overflow-hidden">
          {units.map((unit, idx) => (
            <div key={unit.id} className={`flex items-center justify-between px-4 py-3 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
              <div>
                <span className="font-medium">Daire {unit.unit_number}</span>
                <span className="text-gray-500 text-sm ml-2">— {unit.owner_name}</span>
                {unit.late_fee_exempt && <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">Faizden kalıcı muaf</span>}
              </div>
              <button onClick={() => handleExemptToggle(unit)} disabled={exemptLoading}
                className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${unit.late_fee_exempt ? "bg-green-100 hover:bg-green-200 text-green-700" : "bg-red-100 hover:bg-red-200 text-red-700"}`}>
                {unit.late_fee_exempt ? "Muafiyeti Kaldır" : "Muaf Et"}
              </button>
            </div>
          ))}
        </div>
      </Modal>

      {/* AY BAZLI MUAFİYET MODAL */}
      <Modal show={showMonthExemptModal} onClose={() => setShowMonthExemptModal(false)} title="📅 Ay Bazlı Faiz Muafiyeti">
        <p className="text-sm text-gray-500 mb-4">Seçili ay için gecikme faizi uygulanmaz.</p>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div><label className="block text-xs font-medium mb-1">Daire</label>
            <select value={exemptUnit} onChange={(e) => setExemptUnit(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">— Seçin —</option>
              <option value="ALL">🏢 Tüm Daireler</option>
              {units.map(u => <option key={u.id} value={u.id}>D{u.unit_number} - {u.owner_name}</option>)}
            </select>
          </div>
          <div><label className="block text-xs font-medium mb-1">Ay</label>
            <select value={exemptMonth} onChange={(e) => setExemptMonth(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">— Seçin —</option>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{MONTHS[m-1]}</option>)}
            </select>
          </div>
          <div><label className="block text-xs font-medium mb-1">Yıl</label>
            <input type="number" value={exemptYear} onChange={(e) => setExemptYear(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
        </div>
        <div className="mb-3">
          <label className="block text-xs font-medium mb-1">Not (opsiyonel)</label>
          <input type="text" value={exemptNote} onChange={(e) => setExemptNote(e.target.value)} placeholder="ör: Yeni taşınma anlaşması" className="w-full border rounded px-3 py-2 text-sm" />
        </div>
        <button onClick={handleAddExemption} className="w-full bg-purple-600 text-white py-2 rounded hover:bg-purple-700 text-sm font-semibold mb-4">+ Muafiyet Ekle</button>
        <div className="border rounded overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Mevcut Muafiyetler</div>
          {exemptions.length === 0 ? <p className="text-sm text-gray-400 px-4 py-3">Muafiyet yok.</p>
            : exemptions.map((e) => (
              <div key={e.id} className="flex items-center justify-between px-4 py-2 border-t">
                <div className="text-sm">
                  <span className="font-medium">D{e.unit_number}</span>
                  <span className="text-gray-500 ml-1">— {e.owner_name}</span>
                  <span className="ml-2 text-purple-600 font-semibold">{MONTHS[e.exempt_month - 1]} {e.exempt_year}</span>
                  {e.note && <span className="ml-2 text-gray-400 text-xs italic">"{e.note}"</span>}
                </div>
                <button onClick={() => handleDeleteExemption(e.id)} className="text-red-500 hover:text-red-700 text-xs ml-2">Sil</button>
              </div>
            ))}
        </div>
      </Modal>

    </div>
  );
}

export default ManagerDashboard;